import { useState } from "react";
import { Copy, Pause, Play, Trash2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { AUDIENCES } from "@/components/PromoCreateForm";

const STATUS_CLS = { active: "bg-[#00E5A3]/15 text-[#00E5A3]", paused: "bg-slate-500/20 text-slate-300", expired: "bg-[#FF3B6B]/15 text-[#FF7A7A]", "used up": "bg-[#FFB800]/15 text-[#FFB800]" };
const inp = "bg-[#0B0F19] border border-[#26334D] rounded-lg px-2 py-1 text-sm outline-none focus:border-[#00E5A3] [color-scheme:dark]";

export function PromoRow({ promo, onChanged }) {
  const [uses, setUses] = useState(promo.max_uses);
  const [expires, setExpires] = useState(promo.expires);
  const [open, setOpen] = useState(false);
  const c = promo.code;
  const dirty = Number(uses) !== promo.max_uses || expires !== promo.expires;

  const patch = async (body, msg) => {
    try { await api.patch(`/admin/promos/${c}`, body); toast.success(msg); onChanged(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete code ${c}? People who already used it keep their free access.`)) return;
    try { await api.delete(`/admin/promos/${c}`); toast.success(`Deleted ${c}`); onChanged(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const copy = async () => { await navigator.clipboard.writeText(c).catch(() => {}); toast.success(`Copied ${c}`); };

  return (
    <div data-testid={`promo-row-${c}`} className="rounded-2xl bg-[#0B0F19]/60 border border-[#26334D] p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <button data-testid={`promo-copy-${c}`} onClick={copy} className="font-mono-vv text-lg font-bold tracking-widest flex items-center gap-2 hover:text-[#00E5A3]">{c} <Copy className="h-3.5 w-3.5" /></button>
        <span data-testid={`promo-status-${c}`} className={`text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full ${STATUS_CLS[promo.status]}`}>{promo.status}</span>
        <span className="text-xs text-slate-400">{AUDIENCES.find(([v]) => v === promo.audience)?.[1]}</span>
        <span data-testid={`promo-uses-${c}`} className="ml-auto text-sm text-slate-300">{promo.used} of {promo.max_uses} used</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
        Uses <input data-testid={`promo-edit-uses-${c}`} type="number" min={1} value={uses} onChange={(e) => setUses(e.target.value)} className={`${inp} w-20`} />
        Expires <input data-testid={`promo-edit-expires-${c}`} type="date" value={expires} onChange={(e) => setExpires(e.target.value)} className={inp} />
        {expires && <button onClick={() => setExpires("")} className="underline">never</button>}
        {dirty && <Button data-testid={`promo-save-${c}`} size="sm" onClick={() => patch({ max_uses: Number(uses), expires }, "Saved")} className="rounded-full bg-[#00E5A3] text-[#0B0F19] hover:bg-[#00C98F]">Save</Button>}
        <div className="ml-auto flex gap-1">
          <Button data-testid={`promo-toggle-${c}`} size="sm" variant="ghost" onClick={() => patch({ active: !promo.active }, promo.active ? "Paused" : "Resumed")}>
            {promo.active ? <><Pause className="h-3.5 w-3.5 mr-1" /> Pause</> : <><Play className="h-3.5 w-3.5 mr-1" /> Resume</>}
          </Button>
          <Button data-testid={`promo-delete-${c}`} size="sm" variant="ghost" onClick={remove}><Trash2 className="h-3.5 w-3.5 text-[#FF7A7A]" /></Button>
        </div>
      </div>
      {promo.used_by.length > 0 && (
        <div>
          <button data-testid={`promo-users-toggle-${c}`} onClick={() => setOpen(!open)} className="text-xs text-slate-400 flex items-center gap-1">
            Who used it <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          {open && <ul className="mt-1 text-sm text-slate-400 space-y-0.5">{promo.used_by.map((u) => <li key={u.email}>{u.name} · {u.email}</li>)}</ul>}
        </div>
      )}
    </div>
  );
}
